import { TableTr } from '@mantine/core'
import { DataTable, type DataTableProps } from 'mantine-datatable'
import { type RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react'
import classes from './DataGrid.module.css'

export type { DataTableColumn, DataTableSortStatus } from 'mantine-datatable'

export type Density = 'comfortable' | 'compact'

export type DataGridProps<T> = DataTableProps<T> & { density?: Density }

/** Space a column without a set width keeps before the grid scrolls sideways */
const FLEX_MIN_WIDTH = 160
const SELECTION_WIDTH = 44

/** Past this many rows a page draws only the rows in view: a list's page may hold 500 */
const WINDOW_FROM = 200
/** Rows drawn past each edge of the view, so a scroll shows rows already there */
const OVERSCAN = 40

const shown = (query?: unknown) => typeof query !== 'string' || typeof window === 'undefined' || window.matchMedia(query).matches

interface RowWindow {
  start: number
  end: number
  /** A drawn row's height, measured: what an undrawn one stands in for */
  rowHeight: number
}

/**
 * The rows of a long page in view in the grid's own scrolling area, and a screen's worth either side. It moves on only
 * once the view nears the edge of what is drawn, so scrolling within it draws nothing new
 */
function useRowWindow(count: number, on: boolean, density: Density, viewport: RefObject<HTMLDivElement | null>) {
  const guess = density === 'compact' ? 33 : 45
  const [range, setRange] = useState<RowWindow>({ start: 0, end: Math.min(count, OVERSCAN * 2), rowHeight: guess })
  const current = useRef(range)
  current.current = range

  // A drawn row's real height, once there is one
  useLayoutEffect(() => {
    if (!on) return
    const row = viewport.current?.querySelector<HTMLElement>('tbody tr.mantine-datatable-row')
    const height = row?.offsetHeight
    if (height && Math.abs(height - current.current.rowHeight) > 0.5) setRange((r) => ({ ...r, rowHeight: height }))
  })

  useEffect(() => {
    const element = viewport.current
    if (!on || !element) return
    let frame = 0
    const place = () => {
      frame = 0
      const { start, end, rowHeight } = current.current
      const head = element.querySelector('thead')?.offsetHeight ?? 0
      const first = Math.max(0, Math.floor((element.scrollTop - head) / rowHeight))
      const last = Math.min(count, Math.ceil((element.scrollTop + element.clientHeight) / rowHeight))
      // The view still inside what is drawn, with room to scroll either way (or the page's edge there): nothing to draw
      const roomAbove = start === 0 || first - start >= OVERSCAN / 4
      const roomBelow = end === count || end - last >= OVERSCAN / 4
      if (end <= count && first >= start && last <= end && roomAbove && roomBelow) return
      const next = { start: Math.max(0, first - OVERSCAN), end: Math.min(count, last + OVERSCAN), rowHeight }
      if (next.start !== start || next.end !== end) setRange(next)
    }
    const ask = () => {
      if (!frame) frame = requestAnimationFrame(place)
    }
    place()
    element.addEventListener('scroll', ask, { passive: true })
    const resized = new ResizeObserver(ask)
    resized.observe(element)
    return () => {
      element.removeEventListener('scroll', ask)
      resized.disconnect()
      cancelAnimationFrame(frame)
    }
  }, [on, count, viewport])

  return range
}

/**
 * mantine-datatable with the platform's look: no outer border, quiet
 * header, Material state colours and the user's density preference.
 * Columns with a width keep it; the others share the rest of the row and
 * cut long text with an ellipsis, so the grid fits the screen until too
 * many columns are switched on. A page of more than `WINDOW_FROM` rows draws
 * only the ones in view (`useRowWindow`): the others stand as one empty row
 * above and one below, as tall as they would be, while selecting, sorting and
 * clicking still see every record.
 */
export function DataGrid<T>({ density = 'comfortable', ...props }: DataGridProps<T>) {
  const columns = 'columns' in props ? (props.columns ?? []) : []
  const minWidth = columns
    .filter((c) => !c.hidden && shown(c.visibleMediaQuery))
    .reduce((sum, c) => sum + (typeof c.width === 'number' ? c.width : FLEX_MIN_WIDTH), props.onSelectedRecordsChange ? SELECTION_WIDTH : 0)
  const count = props.records?.length ?? 0
  const windowed = count > WINDOW_FROM && !props.rowFactory
  const ownViewport = useRef<HTMLDivElement | null>(null)
  const viewport = props.scrollViewportRef ?? ownViewport
  const { start, end, rowHeight } = useRowWindow(count, windowed, density, viewport)
  const span = columns.filter((c) => !c.hidden).length + (props.onSelectedRecordsChange ? 1 : 0)
  const gap = (rows: number) =>
    rows > 0 ? (
      <tr className={classes.gap}>
        <td colSpan={span} style={{ height: rows * rowHeight }} />
      </tr>
    ) : null
  const merged = {
    withTableBorder: false,
    borderRadius: 0,
    highlightOnHover: true,
    // The 8% state layer mixed onto the sheet, not see-through: cells inherit the row's
    // background, so a translucent one would let text show under the sticky checkbox column
    highlightOnHoverColor: 'color-mix(in srgb, var(--tnn-on-surface) 8%, var(--tnn-sheet))',
    rowBorderColor: 'var(--tnn-outline-variant)',
    borderColor: 'var(--tnn-outline-variant)',
    backgroundColor: 'var(--tnn-sheet)',
    verticalSpacing: density === 'compact' ? 5 : 11,
    horizontalSpacing: 12,
    fz: 'sm',
    paginationSize: 'md',
    loaderType: 'dots',
    loaderBackgroundBlur: 0,
    classNames: { root: classes.root, table: classes.table, header: classes.header, pagination: classes.pagination },
    styles: { table: { minWidth } },
    ...props,
    scrollViewportRef: viewport,
    ...(windowed && {
      rowFactory: ({ index, rowProps, children, expandedElement }) => {
        if (index === 0 && start > 0) return gap(start)
        if (index === end) return gap(count - end)
        if (index < start || index >= end) return null
        return (
          <>
            <TableTr {...rowProps}>{children}</TableTr>
            {expandedElement}
          </>
        )
      },
    }),
  } as DataTableProps<T>
  return <DataTable<T> {...merged} />
}
